import { Routes } from '@common/constants';
import { useAppContext } from '@common/contexts/AppContext';
import { useLabels } from '@features/Menu/useLabels';
import { useModuleStatuses, usePropertyModules } from '@features/properties';
import { useRoomPageSettings } from '@features/rooms';
import {
  useGetSpaClassesPage,
  useGetSpaDaysPage,
  useGetSpaFacilitiesPage,
  useGetSpaTreatmentsPage,
} from '@features/spa/hooks';
import { useGetTablesPage } from '@features/tables/hooks/useGetTablesPage';
import { useTranslations } from '@lang';
import { FC, useMemo } from 'react';
import { useRouter } from 'shared-routing';
import { CategoryItem, HomeTemplate } from 'voyager';
import { useFeaturedModuleVisibility } from '../useFeaturedModuleVisibility';
import { HomeV2Props } from './HomeV2.types';

export const HomeV2: FC<HomeV2Props> = ({ property, language }) => {
  const router = useRouter();
  const { t } = useTranslations();
  const labels = useLabels();
  const { siteId } = useAppContext();

  const { data: modules } = usePropertyModules(siteId);
  const { isFeaturedSpaDaysVisible, isFeaturedSpaTreatmentsVisible } =
    useFeaturedModuleVisibility();

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();

  const isSpaEnabled = modules?.spa?.enabled ?? false;
  const isClassesVisible = isSpaEnabled && (modules?.spa?.features?.classes?.enabled ?? false);
  const isFacilitiesVisible =
    isSpaEnabled && (modules?.spa?.features?.facilities?.enabled ?? false);
  const isRetailVisible = property.isRetailEnabled;

  const { content: roomsPage } = useRoomPageSettings(language, property.name);
  const { data: spaDaysPage } = useGetSpaDaysPage(property.id, language);
  const { data: spaTreatmentsPage } = useGetSpaTreatmentsPage(property.id, language);
  const { data: spaClassesPage } = useGetSpaClassesPage(property.id, language);
  const { data: spaFacilitiesPage } = useGetSpaFacilitiesPage(property.id, language);
  const { data: tablesPage } = useGetTablesPage(property.id, language);

  const categories = useMemo((): CategoryItem[] => {
    const items: CategoryItem[] = [];

    if (isRoomsEnabled) {
      items.push({
        id: 'rooms',
        label: labels?.rooms || t('home.rooms'),
        imageUrl: roomsPage?.heroBackgroundImage,
      });
    }

    if (property.isTablesIntegrated) {
      items.push({
        id: 'tables',
        label: labels?.tables || t('app.dining'),
        imageUrl: tablesPage?.heroBackgroundImage?.url,
      });
    }

    if (isFeaturedSpaDaysVisible) {
      items.push({
        id: 'spa-days',
        label: labels?.spadays || t('home.spaDays'),
        imageUrl: spaDaysPage?.heroBackgroundImage?.url,
      });
    }

    if (isFeaturedSpaTreatmentsVisible) {
      items.push({
        id: 'spa-treatments',
        label: labels?.treatments || t('home.spaTreatments'),
        imageUrl: spaTreatmentsPage?.heroBackgroundImage?.url,
      });
    }

    if (isFacilitiesVisible) {
      items.push({
        id: 'facilities',
        label: labels?.facilities || t('app.facilities'),
        imageUrl: spaFacilitiesPage?.heroBackgroundImage?.url,
      });
    }

    if (isClassesVisible) {
      items.push({
        id: 'spa-classes',
        label: labels?.classes || t('common.classes'),
        imageUrl: spaClassesPage?.heroBackgroundImage?.url,
      });
    }

    if (isRetailVisible) {
      items.push({
        id: 'products',
        label: labels?.retail || t('category.shop'),
      });
    }

    return items;
  }, [
    isRoomsEnabled,
    isFeaturedSpaDaysVisible,
    isFeaturedSpaTreatmentsVisible,
    isClassesVisible,
    isFacilitiesVisible,
    isRetailVisible,
    property.isTablesIntegrated,
    labels,
    t,
    roomsPage,
    spaDaysPage,
    spaTreatmentsPage,
    spaClassesPage,
    spaFacilitiesPage,
    tablesPage,
  ]);

  const handleSelectCategory = (id: string) => {
    const routeMap: Record<string, string> = {
      rooms: Routes.ROOM_LIST,
      'spa-days': Routes.SPA_DAY_LIST,
      'spa-treatments': Routes.SPA_TREATMENT_LIST,
      'spa-classes': Routes.SPA_CLASSES_LIST,
      facilities: Routes.FACILITY_LIST,
      tables: Routes.TABLES_HOME,
      products: Routes.PRODUCT_LIST,
    };

    const route = routeMap[id];
    if (route) {
      router.push({ pathname: route });
    }
  };

  return <HomeTemplate categories={categories} onSelectCategory={handleSelectCategory} />;
};
